import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ExpenseItemPage,
  ExpenseItemWithRelations,
  CreateExpenseItemInput,
  UpdateExpenseItemInput,
  CreateExpensePaymentInput,
  CreateCatalogItemInput,
  UpdateCatalogItemInput,
  InstallmentInput,
} from '@peles/shared';
import * as expensesApi from '../api/expenses';
import type { ExpenseConfig } from '../api/expenses';

/**
 * Whenever an expense item or its payments change, the client ledger
 * (per-event totals + cross-event receivables) becomes stale because
 * `isProducerFunded`, `clientPaid`, and `clientBalance` are derived from
 * the expense's payments. Invalidate every related query in one place.
 */
function invalidateExpenseAndLedgerQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['expenses'] });
  queryClient.invalidateQueries({ queryKey: ['clientPayments'] });
  queryClient.invalidateQueries({ queryKey: ['clientReceivables'] });
}

export function useExpenseConfig() {
  return useQuery<ExpenseConfig>({
    queryKey: ['expenseConfig'],
    queryFn: () => expensesApi.getExpenseConfig(),
    staleTime: Infinity,
  });
}

export function useExpenseItems(filters?: { eventId?: string; vendorId?: string } | string) {
  // Backward compat: accept string (eventId) or object
  const filterObj = typeof filters === 'string' ? { eventId: filters } : filters;
  return useQuery<ExpenseItemWithRelations[]>({
    queryKey: ['expenses', filterObj ?? 'all'],
    queryFn: () => expensesApi.getExpenseItems(filterObj),
  });
}

/**
 * One page of the expenses list. The previous page stays on screen while the
 * next loads, so paging or narrowing a filter never flashes an empty table.
 */
export function useExpenseItemPage(params: expensesApi.ExpenseListParams) {
  return useQuery<ExpenseItemPage>({
    queryKey: ['expenses', 'page', params],
    queryFn: () => expensesApi.getExpenseItemPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useExpenseItem(id: string) {
  return useQuery<ExpenseItemWithRelations>({
    queryKey: ['expenses', 'detail', id],
    queryFn: () => expensesApi.getExpenseItem(id),
    enabled: !!id,
  });
}

export function useCreateExpenseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExpenseItemInput) => expensesApi.createExpenseItem(data),
    onSuccess: () => invalidateExpenseAndLedgerQueries(queryClient),
  });
}

export function useUpdateExpenseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateExpenseItemInput }) =>
      expensesApi.updateExpenseItem(id, updates),
    onSuccess: (updated) => {
      queryClient.setQueryData(['expenses', 'detail', updated.id], updated);
      invalidateExpenseAndLedgerQueries(queryClient);
    },
  });
}

export function useDeleteExpenseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      args: string | { id: string; items?: { moveItemsTo: string } | { deleteItems: true } },
    ) =>
      typeof args === 'string'
        ? expensesApi.deleteExpenseItem(args)
        : expensesApi.deleteExpenseItem(args.id, args.items),
    onSuccess: () => {
      invalidateExpenseAndLedgerQueries(queryClient);
      // Its items moved to another line, or went with it.
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

export function useAddExpensePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseItemId, ...input }: CreateExpensePaymentInput) =>
      expensesApi.addExpensePayment(expenseItemId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', 'detail', variables.expenseItemId] });
      invalidateExpenseAndLedgerQueries(queryClient);
    },
  });
}

/** Replace a line's payment schedule. */
export function useReplaceInstallments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseItemId,
      installments,
    }: {
      expenseItemId: string;
      installments: InstallmentInput[];
    }) => expensesApi.replaceInstallments(expenseItemId, installments),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', 'detail', variables.expenseItemId] });
      invalidateExpenseAndLedgerQueries(queryClient);
    },
  });
}

/** "Mark paid in full": the server records what is left as one payment. */
export function useMarkPaidInFull() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseItemId,
      ...input
    }: {
      expenseItemId: string;
      paymentMethodId: string;
      paidAt?: string;
      notes?: string;
    }) => expensesApi.markPaidInFull(expenseItemId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', 'detail', variables.expenseItemId] });
      invalidateExpenseAndLedgerQueries(queryClient);
    },
  });
}

export function useRemoveExpensePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseItemId, paymentId }: { expenseItemId: string; paymentId: string }) =>
      expensesApi.removeExpensePayment(expenseItemId, paymentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', 'detail', variables.expenseItemId] });
      invalidateExpenseAndLedgerQueries(queryClient);
    },
  });
}

export function useCreateCatalogItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCatalogItemInput) => expensesApi.createCatalogItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseConfig'] });
    },
  });
}

export function useUpdateCatalogItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateCatalogItemInput }) =>
      expensesApi.updateCatalogItem(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseConfig'] });
    },
  });
}
